import { useContext, useMemo } from 'react';
import { impossibleState } from '@shared/helpers/impossibleState';
import { selectVersion } from '@shared/helpers/versionSelection.helper';
import { useSearchParams } from '@shared/hooks/useSearchParams.hook';
import { AccessType } from '@modules/access/types';
import SignInContext from '@modules/users/contexts/SignInContext.context';
import { USER_GROUPS } from '@modules/users/types/users.types';
import { CHANGE_TEMPLATE_VERSION_PARAM } from '../constants';
import {
  isTChangeTemplateVersion,
  type TChangeTemplateDetails,
  type TChangeTemplateDraft,
  type TChangeTemplateVersion,
} from '../types';

export const useChangeTemplateView = (
  changeTemplate: TChangeTemplateDetails['changeTemplate'],
  accessRight: AccessType
) => {
  const { user } = useContext(SignInContext);
  const { searchParams } = useSearchParams();
  const view = searchParams.get(CHANGE_TEMPLATE_VERSION_PARAM);
  const { draft, versions } = changeTemplate;

  return useMemo(() => {
    const selected =
      selectVersion<TChangeTemplateDraft, TChangeTemplateVersion>({
        draft,
        versions,
        view,
        canEdit: accessRight >= AccessType.EDIT,
        isAuthor: user.groups.includes(USER_GROUPS.AUTHOR),
      }) ?? impossibleState('change template without a draft or a version');
    // The current draft or version is shown without a version in the URL.
    const templateVersion =
      selected !== (draft ?? versions[0]) && isTChangeTemplateVersion(selected) ? selected.id : undefined;
    return { selected, templateVersion };
  }, [view, draft, versions, accessRight, user]);
};
